<template>
    <div v-if="isHorizontal && isMobile && !isAulaPage" class="floating-menu-container">
        <!-- Overlay para fechar o menu ao clicar fora -->
        <div 
            v-if="isExpanded" 
            class="floating-menu-overlay" 
            @click.stop="toggleMenu"
        ></div>
        
        <!-- Botões do menu expandido -->
        <div class="floating-menu-items" :class="{ 'expanded': isExpanded }" @click.stop>
            <router-link
                v-for="(item, index) in menuItems"
                :key="item.path"
                :to="item.path"
                class="floating-menu-item"
                :style="{ 'transition-delay': isExpanded ? `${index * 50}ms` : `${(menuItems.length - index) * 30}ms` }"
                @click.stop="closeMenu"
            >
                <span class="floating-menu-item-label">{{ item.label }}</span>
            </router-link>
        </div>
        
        <!-- Botão principal FAB -->
        <button 
            class="floating-menu-fab" 
            :class="{ 'expanded': isExpanded }"
            @click="toggleMenu"
            aria-label="Menu"
        >
            <svg 
                v-if="!isExpanded"
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
            >
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
            <svg 
                v-else
                xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
            >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

const store = useStore();
const router = useRouter();
const route = useRoute();

const isExpanded = ref(false);
const isMobile = ref(false);

const isHorizontal = computed(() => store.state.menu_style === 'horizontal');
const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);

// Não mostrar o FloatingMenu na tela de aula
const isAulaPage = computed(() => {
    return route.path.includes('/curso/') && route.path.includes('/aula/');
});

const menuItems = computed(() => {
    const items = [{ path: '/', label: 'Inicio' }];
    if (isAuthenticated.value) {
        items.push({ path: '/cursos', label: 'Meus cursos' });
    }
    return items;
});

const checkMobile = () => {
    isMobile.value = window.innerWidth < 992;
};

const toggleMenu = () => {
    isExpanded.value = !isExpanded.value;
};

const closeMenu = () => {
    isExpanded.value = false;
};

onMounted(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Fecha o menu quando a rota mudar
    router.afterEach(() => {
        closeMenu();
    });
});

onUnmounted(() => {
    window.removeEventListener('resize', checkMobile);
});
</script>

<style scoped>
.floating-menu-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
}

.floating-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 9998;
    animation: fadeIn 0.2s ease-out;
    pointer-events: auto;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.floating-menu-items {
    position: absolute;
    bottom: 70px;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 10px;
    z-index: 10000;
    pointer-events: none;
}

.floating-menu-items.expanded {
    pointer-events: auto;
}

.floating-menu-item {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-decoration: none;
    opacity: 0;
    transform: translateY(20px) scale(0.8);
    pointer-events: none;
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    position: relative;
    z-index: 10001;
}

.floating-menu-items.expanded .floating-menu-item {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
}

.floating-menu-item-label {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    color: #3b3f5c;
    white-space: nowrap;
    transition: all 0.2s;
    padding: 10px 16px;
    border-radius: 24px;
    pointer-events: none;
    position: relative;
    z-index: 10001;
}

.floating-menu-item:hover .floating-menu-item:hover .floating-menu-item-label {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.floating-menu-fab {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    color: #fff;
    position: relative;
    z-index: 10002;
    pointer-events: auto;
}

.floating-menu-fab:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.floating-menu-fab:active {
    transform: scale(0.95);
}

.floating-menu-fab.expanded {
    transform: rotate(45deg);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.floating-menu-fab svg {
    width: 24px;
    height: 24px;
    stroke: currentColor;
}

/* Modo escuro */
.dark .floating-menu-item-label {
    background: #1b2e4b;
    color: #e0e6ed;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.dark .floating-menu-item:hover .floating-menu-item-label {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}
</style>
